import React from 'react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { FileText, Briefcase, User, Shield, Upload, AlertTriangle, AlertCircle, XCircle, Scale, RefreshCw } from 'lucide-react';

const TermsOfServicePage = () => {
  const sections = [
    {
      icon: Briefcase,
      title: "Our Services",
      content: [
        {
          subtitle: "Service Offerings",
          text: "Redsage provides a range of technology solutions and services including but not limited to cloud infrastructure management, data analytics and AI implementation, enterprise management solutions, cybersecurity services, and technology transformation consulting."
        },
        {
          subtitle: "Service Modifications",
          text: "We reserve the right to modify, suspend, or discontinue any aspect of our Services at any time, including the availability of any feature, database, or content. We may also impose limits on certain features and services or restrict your access to parts or all of the Services without notice or liability."
        }
      ]
    },
    {
      icon: User,
      title: "Your Account",
      content: [
        {
          subtitle: "Account Responsibility",
          text: "To access certain features of our Services, you may be required to create an account. You are responsible for maintaining the confidentiality of your account and password and for restricting access to your devices. You agree to accept responsibility for all activities that occur under your account."
        },
        {
          subtitle: "Account Information",
          text: "You must provide accurate, current, and complete information during the registration process and keep your account information updated. We reserve the right to suspend or terminate your account if any information provided proves to be inaccurate, not current, or incomplete."
        }
      ]
    },
    {
      icon: Shield,
      title: "Intellectual Property",
      content: [
        {
          subtitle: "Ownership",
          text: "Our Services and all materials therein, including but not limited to software, images, text, graphics, logos, patents, trademarks, service marks, copyrights, photographs, audio, videos, and all intellectual property rights related thereto, are the exclusive property of Redsage or its licensors."
        },
        {
          subtitle: "Restrictions",
          text: "Except as explicitly provided herein, nothing in these Terms shall be deemed to create a license in or under any such intellectual property rights, and you agree not to sell, license, rent, modify, distribute, copy, reproduce, transmit, publicly display, publicly perform, publish, adapt, edit or create derivative works from any materials or content accessible on the Services."
        }
      ]
    },
    {
      icon: Upload,
      title: "User Content",
      content: [
        {
          subtitle: "Your Rights",
          text: "You retain all rights in, and are solely responsible for, the content you post, upload, or otherwise make available via our Services (\"User Content\")."
        },
        {
          subtitle: "License Grant",
          text: "By submitting User Content to Redsage, you grant us a worldwide, non-exclusive, royalty-free, sublicensable, and transferable license to use, reproduce, distribute, prepare derivative works of, display, and perform the User Content in connection with providing and promoting our Services."
        }
      ]
    },
    {
      icon: AlertTriangle,
      title: "Prohibited Activities",
      content: [
        {
          subtitle: "Illegal Activities",
          text: "You agree not to use the Services for any illegal purpose or in violation of any local, state, national, or international law. You must not violate or encourage others to violate the rights of third parties, including intellectual property rights."
        },
        {
          subtitle: "Security Violations",
          text: "You must not upload or transmit viruses, malware, or other types of malicious software. You must not attempt to interfere with, compromise the system integrity or security, or decipher any transmissions to or from the servers running the Services."
        },
        {
          subtitle: "Misrepresentation",
          text: "You must not impersonate another person or otherwise misrepresent your affiliation with a person or entity. You must not attempt to access any part of the Services that you are not authorized to access."
        }
      ]
    },
    {
      icon: AlertCircle,
      title: "Limitation of Liability",
      content: [
        {
          subtitle: "Disclaimer",
          text: "In no event shall Redsage, its officers, directors, employees, or agents, be liable to you for any direct, indirect, incidental, special, punitive, or consequential damages whatsoever resulting from any errors, mistakes, or inaccuracies of content, or personal injury or property damage related to your use of the Services."
        },
        {
          subtitle: "Service Interruptions",
          text: "We are not liable for unauthorized access to or use of our servers and/or any personal information stored therein, interruption or cessation of transmission to or from our Services, bugs, viruses, Trojan horses, or the like transmitted through the Services by any third party."
        },
        {
          subtitle: "Content Liability",
          text: "We are not liable for any loss or damage of any kind incurred as a result of your use of any content posted, emailed, transmitted, or otherwise made available via the Services."
        }
      ]
    },
    {
      icon: XCircle,
      title: "Termination",
      content: [
        {
          subtitle: "Our Rights",
          text: "We may terminate or suspend your account and access to the Services immediately, without prior notice or liability, for any reason, including if you breach these Terms."
        },
        {
          subtitle: "Effect of Termination",
          text: "Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account, you may simply discontinue using the Services or contact us to request account deletion."
        }
      ]
    },
    {
      icon: Scale,
      title: "Governing Law",
      content: [
        {
          subtitle: "Jurisdiction",
          text: "These Terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions."
        },
        {
          subtitle: "Dispute Resolution",
          text: "You agree to submit to the personal and exclusive jurisdiction of the courts located in Hyderabad, Telangana, India for the resolution of any disputes arising out of or relating to these Terms or the Services."
        }
      ]
    },
    {
      icon: RefreshCw,
      title: "Changes to Terms",
      content: [
        {
          subtitle: "Modifications",
          text: "We reserve the right, at our sole discretion, to modify or replace these Terms at any time. The most current version will be posted on our website with the effective date."
        },
        {
          subtitle: "Acceptance",
          text: "Your continued use of the Services after any such changes constitutes your acceptance of the new Terms. If you do not agree to the new terms, you must stop using the Services."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <span className="text-neutral-400 text-sm font-medium uppercase tracking-widest mb-4 block">Legal</span>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-black leading-[0.95] mb-6 tracking-tight">
              Terms of Service
            </h1>
            <p className="text-xl text-neutral-500 leading-relaxed">
              Please read these terms carefully before using our services.
              They govern your access to and use of Redsage&apos;s products and services.
            </p>
            <p className="text-sm text-neutral-400 mt-6">
              Last updated: February 5, 2026
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-neutral-50 border-y border-neutral-200">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-6">
              <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-black mb-3">Introduction</h2>
                <p className="text-neutral-600 leading-relaxed">
                  Welcome to Redsage. By accessing our website, products, or services, you agree to be bound by these
                  Terms of Service. These Terms of Service (&quot;Terms&quot;) govern your access to and use of Redsage&apos;s
                  services, including our website, cloud solutions, data &amp; AI services, cybersecurity products, and
                  other technology transformation services (&quot;Services&quot;).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Sections */}
      <section className="section bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto space-y-16">
            {sections.map((section, index) => (
              <div key={index} className="border-b border-neutral-200 pb-16 last:border-0 last:pb-0">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-12 h-12 bg-neutral-100 rounded-xl flex items-center justify-center">
                    <section.icon className="w-5 h-5 text-black" />
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl text-black tracking-tight">
                    {section.title}
                  </h2>
                </div>
                <div className="space-y-8 pl-0 md:pl-16">
                  {section.content.map((item, itemIndex) => (
                    <div key={itemIndex}>
                      <h3 className="text-black font-semibold mb-2">{item.subtitle}</h3>
                      <p className="text-neutral-600 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsOfServicePage;
